import { useEffect, useRef, useState } from "react";
import { Copy, Check, KeyRound, ShieldAlert, Loader2, MapPinned, MapPin, Building2, Navigation } from "lucide-react";
import { Modal } from "../../components/Modal";
import { Button, Field, Input, Select } from "../../components/ui";
import { createBranch, updateBranch } from "../../api/branches";
import { lookupPincode, isValidPincode } from "../../lib/pincodeLookup";
import { useAuth } from "../../context/AuthContext";

const emptyForm = {
  name: "",
  phone: "",
  email: "",
  registeredBusinessName: "",
  gstNumber: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  status: "ACTIVE",
};

export default function BranchFormModal({ open, onClose, branch, onSaved }) {
  const { partner: chain } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [credentials, setCredentials] = useState(null);
  
  // Pincode state
  const [pincodeStatus, setPincodeStatus] = useState(null); // null | "looking" | "found" | "not-found"
  const [pincodeData, setPincodeData] = useState(null);
  const [localities, setLocalities] = useState([]);
  const [selectedLocality, setSelectedLocality] = useState("");
  const pincodeLookupToken = useRef(0);
  const [isPincodeAutoFilled, setIsPincodeAutoFilled] = useState(false);
  const [showLocationDetails, setShowLocationDetails] = useState(false);

  const isEdit = !!branch;

  useEffect(() => {
    if (!open) return;
    setForm(
      branch
        ? {
            name: branch.name ?? "",
            phone: branch.phone ?? "",
            email: branch.email ?? "",
            registeredBusinessName: branch.registeredBusinessName ?? "",
            gstNumber: branch.gstNumber ?? "",
            address: branch.location?.address ?? "",
            city: branch.location?.city ?? "",
            state: branch.location?.state ?? "",
            pincode: branch.location?.pincode ?? "",
            status: branch.status ?? "ACTIVE",
          }
        : { 
            ...emptyForm, 
            registeredBusinessName: chain?.registeredBusinessName ?? "" 
          }
    );
    setErrors({});
    setFormError("");
    setCredentials(null);
    setPincodeStatus(null);
    setPincodeData(null);
    setLocalities([]);
    setSelectedLocality("");
    setIsPincodeAutoFilled(false);
    setShowLocationDetails(!!branch?.location?.pincode || false);
  }, [open, branch, chain]);

  // Debounced pincode lookup
  useEffect(() => {
    if (!open) return;
    const pincode = form.pincode.trim();
    
    // Reset if pincode is empty or invalid
    if (!pincode || !isValidPincode(pincode)) {
      setPincodeStatus(null);
      setPincodeData(null);
      setLocalities([]);
      setSelectedLocality("");
      setIsPincodeAutoFilled(false);
      setShowLocationDetails(false);
      return;
    }

    const token = ++pincodeLookupToken.current;
    setPincodeStatus("looking");
    setShowLocationDetails(true);
    
    const t = setTimeout(async () => {
      const offices = await lookupPincode(pincode);
      if (pincodeLookupToken.current !== token) return;
      
      if (!offices || offices.length === 0) {
        setPincodeStatus("not-found");
        setPincodeData(null);
        setLocalities([]);
        setIsPincodeAutoFilled(false);
        return;
      }
      
      setPincodeStatus("found");
      setPincodeData(offices[0]);
      setLocalities(offices);
      
      // Auto-fill with the first office
      applyLocality(offices[0]);
      setIsPincodeAutoFilled(true);
    }, 500);
    
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.pincode]);

  function applyLocality(office) {
    if (!office) return;
    
    setForm((f) => ({
      ...f,
      city: office.District || f.city,
      state: office.State || f.state,
    }));
    setSelectedLocality(office.Name || "");
  }

  // Handle pincode change
  function handlePincodeChange(value) {
    const cleanPincode = value.replace(/\D/g, "").slice(0, 6);
    setForm((f) => ({ ...f, pincode: cleanPincode }));
    
    // Reset pincode-related states
    setPincodeStatus(null);
    setPincodeData(null);
    setLocalities([]);
    setSelectedLocality("");
    setIsPincodeAutoFilled(false);
    
    // Clear auto-filled location fields if pincode is being changed
    if (form.pincode && form.pincode !== cleanPincode) {
      setForm((f) => ({
        ...f,
        city: "",
        state: "",
      }));
      setShowLocationDetails(false);
    }
  }

  function update(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  function buildLocation() {
    const { address, city, state, pincode } = form;
    if (!address && !city && !state && !pincode) return undefined;
    return {
      ...(address ? { address } : {}),
      ...(city ? { city } : {}),
      ...(state ? { state } : {}),
      ...(pincode ? { pincode } : {}),
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError("");
    setErrors({});

    // Validate pincode if provided
    if (form.pincode) {
      if (!isValidPincode(form.pincode)) {
        setErrors((e) => ({ ...e, "location.pincode": "Please enter a valid 6-digit pincode" }));
        return;
      }
      
      // Ensure city and state are filled if pincode is provided
      if (!form.city || !form.state) {
        setErrors((e) => ({ 
          ...e, 
          "location.city": "City is required when pincode is provided",
          "location.state": "State is required when pincode is provided"
        }));
        return;
      }
    }

    const gstValue = form.gstNumber.trim().toUpperCase();

    setSaving(true);
    try {
      const location = buildLocation();
      if (isEdit) {
        const payload = {
          name: form.name.trim(),
          registeredBusinessName: form.registeredBusinessName.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim(),
          gstNumber: gstValue || undefined,
          status: form.status,
          ...(location ? { location } : {}),
        };
        await updateBranch(branch._id, payload);
        onSaved();
      } else {
        const payload = {
          name: form.name.trim(),
          phone: form.phone.trim(),
          ...(form.email.trim() ? { email: form.email.trim() } : {}),
          ...(form.registeredBusinessName.trim()
            ? { registeredBusinessName: form.registeredBusinessName.trim() }
            : {}),
          ...(gstValue ? { gstNumber: gstValue } : {}),
          ...(location ? { location } : {}),
        };
        const res = await createBranch(payload);
        setCredentials({
          username: res.data.username,
          generatedPassword: res.data.generatedPassword,
          name: res.data.name,
        });
      }
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      if (apiErrors?.length) {
        const map = {};
        apiErrors.forEach((er) => (map[er.path] = er.message));
        setErrors(map);
      } else {
        setFormError(err?.response?.data?.message || "Something went wrong. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  }

  function finishAfterCredentials() {
    setCredentials(null);
    onSaved();
  }

  if (credentials) {
    return (
      <Modal
        open={open}
        onClose={finishAfterCredentials}
        title="Branch onboarded"
        description={`Save these credentials for ${credentials.name} — the password can't be retrieved again.`}
        className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
            <ShieldAlert size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <p className="text-sm text-amber-800">
              Hand these credentials to the branch now. They're shown only once and can't be
              recovered — only reset via "Forgot password".
            </p>
          </div>

          <CredentialRow label="Username" value={credentials.username} />
          <CredentialRow label="Password" value={credentials.generatedPassword} />

          <Button
            onClick={finishAfterCredentials}
            className="w-full bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25"
          >
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit branch" : "Onboard a branch"}
      description={
        isEdit
          ? `Updating ${branch.name}'s details.`
          : "Add a new outlet under your chain. Credentials are generated automatically."
      }
      className="bg-white/95 backdrop-blur-sm border border-emerald-100 shadow-2xl shadow-emerald-200/50"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {formError && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3.5 py-3 text-sm text-red-700">
            {formError}
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Branch name" required error={errors.name}>
              <Input
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                placeholder="Enter branch name"
                required
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
            </Field>
          </div>

          <Field label="Phone number" required error={errors.phone} hint="10-digit Indian mobile number">
            <Input
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
              placeholder="9812345678"
              inputMode="numeric"
              maxLength={10}
              required
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>

          <Field label="Email" error={errors.email}>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="branch@example.com"
              className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
            />
          </Field>

          <div className="sm:col-span-2">
            <Field
              label="Registered business name"
              error={errors.registeredBusinessName}
              hint="Defaults to the chain's registered name if left blank"
            >
              <Input
                value={form.registeredBusinessName}
                onChange={(e) => update("registeredBusinessName", e.target.value)}
                placeholder="Dash Entertainment Pvt Ltd"
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              />
            </Field>
          </div>

          {/* GST is a plain optional field. It used to be checked against a
              Cashfree lookup before saving, which blocked onboarding whenever
              that service was unavailable — the API itself only asks for the
              number (§3.1), so it is simply passed through. */}
          <div className="sm:col-span-2">
            <Field
              label="GST number"
              error={errors.gstNumber}
              hint={!errors.gstNumber ? "Optional" : undefined}
            >
              <Input
                value={form.gstNumber}
                onChange={(e) => update("gstNumber", e.target.value.toUpperCase())}
                placeholder="29ABCDE1234F1Z5"
                maxLength={15}
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm font-mono uppercase"
              />
            </Field>
          </div>

          {isEdit && (
            <Field label="Status" error={errors.status}>
              <Select
                value={form.status}
                onChange={(e) => update("status", e.target.value)}
                className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
              >
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="SUSPENDED">Suspended</option>
              </Select>
            </Field>
          )}
        </div>

        {/* Location Section */}
        <div className="rounded-xl border border-gray-200 p-3.5 sm:p-4">
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={16} className="text-emerald-600" />
            <p className="text-sm font-semibold text-gray-700">Location Details</p>
            {form.pincode && pincodeStatus === "found" && (
              <span className="ml-auto text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                ✓ Verified
              </span>
            )}
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Pincode"
              error={errors["location.pincode"]}
              hint="Enter 6-digit pincode to auto-fill location"
            >
              <div className="relative">
                <Input
                  value={form.pincode}
                  onChange={(e) => handlePincodeChange(e.target.value)}
                  placeholder="110001"
                  inputMode="numeric"
                  maxLength={6}
                  className={`border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm pr-9 ${
                    pincodeStatus === "found" ? "border-emerald-400 bg-emerald-50/30" : ""
                  } ${pincodeStatus === "not-found" ? "border-amber-400 bg-amber-50/30" : ""}`}
                />
                {pincodeStatus === "looking" && (
                  <Loader2 size={16} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" />
                )}
                {pincodeStatus === "found" && (
                  <MapPinned size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500" />
                )}
                {pincodeStatus === "not-found" && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-amber-500 text-xs font-medium">?</span>
                )}
              </div>
              {pincodeStatus === "found" && pincodeData && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-emerald-700">
                  <Building2 size={12} />
                  <span>{pincodeData.District}, {pincodeData.State}</span>
                </div>
              )}
              {pincodeStatus === "not-found" && (
                <p className="mt-1 text-xs text-amber-600">
                  Couldn't find this pincode. Please enter city and state manually.
                </p>
              )}
            </Field>

            {/* Show location details only when pincode is entered */}
            {showLocationDetails && (
              <>
                {/* Locality selector - shown when multiple offices found */}
                {localities.length > 1 && (
                  <Field label="Locality" hint="Multiple areas share this pincode">
                    <Select
                      value={selectedLocality}
                      onChange={(e) => {
                        const office = localities.find((o) => o.Name === e.target.value);
                        if (office) {
                          applyLocality(office);
                          setIsPincodeAutoFilled(true);
                        }
                      }}
                      className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm"
                    >
                      <option value="" disabled>Select a locality…</option>
                      {localities.map((o) => (
                        <option key={o.Name} value={o.Name}>
                          {o.Name} {o.District ? `(${o.District})` : ""}
                        </option>
                      ))}
                    </Select>
                  </Field>
                )}

                <div className="sm:col-span-2">
                  <Field 
                    label="Address" 
                    error={errors["location.address"]}
                    hint="Street address, building name, etc. (Editable)"
                  >
                    <div className="relative">
                      <Navigation size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        value={form.address}
                        onChange={(e) => update("address", e.target.value)}
                        placeholder="123, Main Street, Area Name"
                        className="border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm pl-9"
                      />
                    </div>
                  </Field>
                </div>
                
                <Field 
                  label="City" 
                  error={errors["location.city"]}
                  required={!!form.pincode}
                >
                  <Input
                    value={form.city}
                    onChange={(e) => update("city", e.target.value)}
                    placeholder="New Delhi"
                    disabled={isPincodeAutoFilled}
                    className={`border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm ${
                      isPincodeAutoFilled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                  />
                  {isPincodeAutoFilled && (
                    <p className="mt-1 text-xs text-gray-400">
                      Auto-filled from pincode. Change pincode to update.
                    </p>
                  )}
                </Field>
                
                <Field 
                  label="State" 
                  error={errors["location.state"]}
                  required={!!form.pincode}
                >
                  <Input
                    value={form.state}
                    onChange={(e) => update("state", e.target.value)}
                    placeholder="Delhi"
                    disabled={isPincodeAutoFilled}
                    className={`border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm ${
                      isPincodeAutoFilled ? "bg-gray-100 cursor-not-allowed" : ""
                    }`}
                  />
                  {isPincodeAutoFilled && (
                    <p className="mt-1 text-xs text-gray-400">
                      Auto-filled from pincode. Change pincode to update.
                    </p>
                  )}
                </Field>
              </>
            )}
          </div>

          {/* Location summary */}
          {form.address || form.city || form.state || form.pincode ? (
            <div className="mt-3 p-2.5 bg-gray-50 rounded-lg border border-gray-100">
              <p className="text-xs text-gray-500 flex items-center gap-1.5">
                <MapPin size={12} />
                <span>📍 Location: </span>
              </p>
              <p className="text-sm text-gray-700 mt-0.5">
                {[form.address, form.city, form.state, form.pincode].filter(Boolean).join(", ")}
              </p>
            </div>
          ) : (
            <p className="mt-3 text-xs text-gray-400 text-center">
              Enter pincode to auto-fill location details
            </p>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-2.5 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={saving}
            className="bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg hover:shadow-emerald-500/25 w-full sm:w-auto"
          >
            {isEdit ? "Save changes" : "Onboard branch"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function CredentialRow({ label, value }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API unavailable
    }
  }

  return (
    <div className="flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-4 py-3">
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-xs text-gray-500">
          <KeyRound size={12} /> {label}
        </p>
        <p className="mt-0.5 truncate font-mono text-base font-semibold text-gray-800">{value}</p>
      </div>
      <button
        type="button"
        onClick={copy}
        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-emerald-200 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-50 transition-colors"
      >
        {copied ? <Check size={13} /> : <Copy size={13} />}
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}